import { clearExpiredDemoSignIn } from '@/lib/demoSignInExpiry'
import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  clearMemberLearning,
  replaceMemberSession,
} from '@/components/member-portal/memberPortalSession'
import type { MemberPortalMembership } from '@/components/member-portal/MemberPortalOverview'
import type { MemberStatusSource } from '@/components/member-portal/memberPortalPresentation'
import {
  SharedEmailChooser,
  type SharedEmailPerson,
} from '@/components/member-portal/SharedEmailChooser'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, postJSON } from '@/lib/api'
import type { Association } from '@/pages/StaffSignInPage'

export interface MemberSessionResponse {
  person: {
    id?: number
    organization_disclosures?: Array<{ id: number; name: string }>
    name: string
    email: string
    email_suppressed?: boolean
    member_access?: { member: boolean }
    membership?: MemberPortalMembership | null
    member_status_sources?: MemberStatusSource[]
    organizations?: MemberPortalOrganization[]
    leader_chapters?: MemberPortalLeaderChapter[]
    blast_paused?: boolean
  }
}

export interface MemberPortalLeaderChapter {
  id: number
  name: string
  archived: boolean
}

export interface MemberPortalOrganization {
  id: number
  name: string
  level: string
  status: string
  renewal_due_on: string | null
  roster_count: number
  roster_cap: number | null
  profile?: boolean
  term_starts_on?: string | null
  permitted_sections?: PortalOrganizationSection[]
}

export type PortalOrganizationSection = 'dashboard' | 'roster' | 'profile' | 'roles'

interface MemberSelectionRequirement {
  selection_token: string
  people: SharedEmailPerson[]
}

type MemberRedemptionResponse = MemberSessionResponse | MemberSelectionRequirement

export function MemberSignIn({
  association,
  expiredDemoLink = false,
  onSignedIn,
}: {
  association: Association
  expiredDemoLink?: boolean
  onSignedIn?: () => void
}) {
  const queryClient = useQueryClient()
  const completeSignIn = (data: MemberSessionResponse) => {
    clearExpiredDemoSignIn()
    replaceMemberSession(queryClient, data)
    onSignedIn?.()
  }
  // The outgoing portal frame is now unmounted and cannot recreate its transcript query.
  React.useEffect(() => clearMemberLearning(queryClient), [queryClient])
  const [email, setEmail] = React.useState('')
  const [sentTo, setSentTo] = React.useState<string | null>(null)
  const [code, setCode] = React.useState(Array.from({ length: 6 }, () => ''))
  const [cooldown, setCooldown] = React.useState(0)
  const [selection, setSelection] = React.useState<MemberSelectionRequirement | null>(null)
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([])

  React.useEffect(() => {
    if (cooldown <= 0) return
    const timer = window.setTimeout(() => setCooldown((value) => value - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [cooldown])

  const requestCode = useMutation({
    mutationFn: (requestedEmail: string) =>
      postJSON('/api/v1/member_portal/sign_in_code', {
        sign_in_code: { email: requestedEmail },
      }),
    onSuccess: () => {
      setSentTo(email)
      setCooldown(30)
    },
  })
  const redeemCode = useMutation({
    mutationFn: () =>
      postJSON<MemberRedemptionResponse>('/api/v1/member_portal/sign_in_code/redemption', {
        sign_in_code: { email: sentTo, code: code.join('') },
      }),
    onSuccess: (data) => {
      if ('selection_token' in data) {
        setSelection(data)
        return
      }
      completeSignIn(data)
    },
  })
  const selectPerson = useMutation({
    mutationFn: ({ token, contactId }: { token: string; contactId: number }) =>
      postJSON<MemberSessionResponse>('/api/v1/member_portal/person_selection', {
        person_selection: { token, contact_id: contactId },
      }),
    onSuccess: completeSignIn,
  })

  if (sentTo && selection) {
    return (
      <SignInFrame association={association}>
        <SharedEmailChooser
          email={sentTo}
          people={selection.people}
          selecting={selectPerson.isPending}
          selectionFailed={selectPerson.isError}
          onSelect={(contactId) =>
            selectPerson.mutate({ token: selection.selection_token, contactId })
          }
        />
      </SignInFrame>
    )
  }

  if (sentTo) {
    return (
      <MemberCodeStep
        association={association}
        email={sentTo}
        code={code}
        inputRefs={inputRefs}
        cooldown={cooldown}
        requesting={requestCode.isPending}
        redeeming={redeemCode.isPending}
        redemptionError={redeemCode.error}
        onCodeChange={setCode}
        onRedeem={() => redeemCode.mutate()}
        onResend={() => requestCode.mutate(sentTo)}
        onUseDifferentEmail={() => {
          setSentTo(null)
          setCode(Array.from({ length: 6 }, () => ''))
        }}
      />
    )
  }

  return (
    <MemberEmailStep
      expiredDemoLink={expiredDemoLink}
      association={association}
      email={email}
      requesting={requestCode.isPending}
      requestFailed={requestCode.isError}
      onEmailChange={setEmail}
      onContinue={() => requestCode.mutate(email)}
    />
  )
}

interface MemberCodeStepProps {
  association: Association
  email: string
  code: string[]
  inputRefs: React.RefObject<Array<HTMLInputElement | null>>
  cooldown: number
  requesting: boolean
  redeeming: boolean
  redemptionError: Error | null
  onCodeChange: React.Dispatch<React.SetStateAction<string[]>>
  onRedeem: () => void
  onResend: () => void
  onUseDifferentEmail: () => void
}

function MemberCodeStep(props: MemberCodeStepProps) {
  const {
    association,
    email,
    code,
    inputRefs,
    cooldown,
    requesting,
    redeeming,
    redemptionError,
    onCodeChange,
    onRedeem,
    onResend,
    onUseDifferentEmail,
  } = props
  return (
    <SignInFrame association={association}>
      <h1 className="text-subheading font-semibold text-foreground">Check your email</h1>
      <p className="mt-1.5 text-body-sm text-muted-foreground">
        We sent a sign-in code to {email}. It expires in 10 minutes.
      </p>
      <form
        className="mt-6"
        onSubmit={(event) => {
          event.preventDefault()
          onRedeem()
        }}
      >
        {redemptionError && <Alert className="mb-4">{redemptionMessage(redemptionError)}</Alert>}
        <CodeInputs code={code} inputRefs={inputRefs} onCodeChange={onCodeChange} />
        <Button type="submit" className="mt-6 w-full" disabled={redeeming}>
          {redeeming ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>
      <div className="mt-4 flex items-center justify-between">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-caption text-muted-foreground"
          disabled={cooldown > 0 || requesting}
          onClick={onResend}
        >
          Resend code{cooldown > 0 ? ` in ${cooldown}s` : ''}
        </Button>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-caption"
          onClick={onUseDifferentEmail}
        >
          Use a different email
        </Button>
      </div>
    </SignInFrame>
  )
}

function CodeInputs({
  code,
  inputRefs,
  onCodeChange,
}: Pick<MemberCodeStepProps, 'code' | 'inputRefs' | 'onCodeChange'>) {
  return (
    <div className="flex gap-2" aria-label="Sign-in code">
      {code.map((digit, index) => (
        <Input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element
          }}
          aria-label={`Code digit ${index + 1}`}
          className="h-12 w-10 text-center text-body font-medium"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          onChange={(event) => {
            const nextDigit = event.target.value.replace(/\D/g, '').slice(-1)
            onCodeChange((current) =>
              current.map((value, digitIndex) => (digitIndex === index ? nextDigit : value)),
            )
            if (nextDigit) inputRefs.current[index + 1]?.focus()
          }}
        />
      ))}
    </div>
  )
}

function MemberEmailStep({
  expiredDemoLink,
  association,
  email,
  requesting,
  requestFailed,
  onEmailChange,
  onContinue,
}: {
  association: Association
  email: string
  requesting: boolean
  requestFailed: boolean
  expiredDemoLink: boolean
  onEmailChange: (value: string) => void
  onContinue: () => void
}) {
  return (
    <SignInFrame association={association}>
      <h1 className="text-subheading font-semibold text-foreground">Sign in</h1>
      <p className="mt-1.5 text-body-sm text-muted-foreground">
        Enter your email and we&apos;ll send you a sign-in code.
      </p>
      <form
        className="mt-6 space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          onContinue()
        }}
      >
        {expiredDemoLink && <Alert>Link expired</Alert>}
        {requestFailed && <Alert>We couldn&apos;t send a code. Please try again.</Alert>}
        <div className="space-y-2">
          <Label htmlFor="member-email">Email</Label>
          <Input
            id="member-email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" disabled={requesting}>
          {requesting ? 'Sending...' : 'Continue'}
        </Button>
      </form>
      <p className="mt-4 text-center text-caption text-muted-foreground">
        Not a member yet?{' '}
        <a className="text-primary underline-offset-2 hover:underline" href="/join">
          Join {association.name}
        </a>
      </p>
    </SignInFrame>
  )
}

function SignInFrame({
  association,
  children,
}: {
  association: Association
  children: React.ReactNode
}) {
  return (
    <main className="relative min-h-screen bg-background">
      <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6">
        <section className="rounded border border-border bg-card p-8">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-md bg-primary text-body font-semibold text-primary-foreground">
              {association.name.slice(0, 1)}
            </span>
            <p className="text-caption text-muted-foreground">{association.name}</p>
          </div>
          {children}
        </section>
      </div>
    </main>
  )
}

function redemptionMessage(error: Error) {
  if (error instanceof ApiError && error.status === 422) {
    return 'That sign-in code is invalid or expired.'
  }
  return 'We couldn’t sign you in. Please try again.'
}
